/**
 * Modular Favorites/Wishlist System
 * Uses LocalStorage for quick access. Minimal, zero-dependency.
 */

const STORAGE_KEY = 'theme_favorites_v1';

class FavoritesManager {
  static getFavorites() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  static isFavorited(handle) {
    return this.getFavorites().some(item => item.handle === handle);
  }

  static toggleFavorite(productData) {
    let favorites = this.getFavorites();
    const index = favorites.findIndex(item => item.handle === productData.handle);
    let isAdded = false;

    if (index > -1) {
      favorites.splice(index, 1);
    } else {
      favorites.push(productData);
      isAdded = true;
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
    window.dispatchEvent(new CustomEvent('favorites:updated', { detail: { favorites } }));
    return isAdded;
  }
}

/**
 * <favorites-button product-handle="x" product-title="y" product-image="z" product-price="10.00"></favorites-button>
 */
class FavoritesButton extends HTMLElement {
  connectedCallback() {
    this.handle = this.getAttribute('product-handle');
    this.title = this.getAttribute('product-title');
    this.image = this.getAttribute('product-image');
    this.price = this.getAttribute('product-price');
    this.url = this.getAttribute('product-url') || `/products/${this.handle}`;
    
    this.render();
    this.addEventListener('click', this.onClick.bind(this));
    
    this.updateHandler = () => this.updateState();
    window.addEventListener('favorites:updated', this.updateHandler);
  }

  disconnectedCallback() {
    window.removeEventListener('favorites:updated', this.updateHandler);
  }

  onClick(e) {
    e.preventDefault();
    e.stopPropagation();
    
    if (!this.handle) return;
    
    FavoritesManager.toggleFavorite({
      handle: this.handle,
      title: this.title,
      image: this.image,
      price: this.price,
      url: this.url
    });
  }

  updateState() {
    this.render();
  }

  render() {
    const isFavorited = FavoritesManager.isFavorited(this.handle);
    const fill = isFavorited ? 'currentColor' : 'none';
    const color = isFavorited ? '#c00' : 'inherit';

    const mode = this.getAttribute('mode');
    
    if (mode === 'text-button') {
      const text = isFavorited ? 'REMOVE FROM WISHLIST' : 'ADD TO WISHLIST';
      this.innerHTML = `
        <button type="button" class="btn-outline-custom favorites-btn" style="flex: 1; display: inline-flex; align-items: center; justify-content: center; padding: 0 24px; border: 1px solid #e0e0e0; background: #ffffff; color: #1a1a1a; font-weight: 600; cursor: pointer; border-radius: 9999px; transition: all 0.2s ease; width: 100%; height: 48px; min-height: 48px; max-height: 48px; line-height: 1; box-sizing: border-box; margin: 0; font-size: 13px; letter-spacing: 0.05em; text-transform: uppercase;">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="${fill}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: ${color}; margin-right: 8px; flex-shrink: 0; display: inline-block; vertical-align: middle;"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
          <span style="display: inline-block; line-height: 1;">${text}</span>
        </button>
      `;
    } else {
      this.innerHTML = `
      <button type="button" class="favorites-btn" aria-label="Toggle Wishlist" style="background-color: #ffffff !important; border: none; cursor: pointer; padding: 6px; border-radius: 50%; box-shadow: 0 2px 6px rgba(0,0,0,0.15); display: flex; align-items: center; justify-content: center; transition: transform 0.2s ease; width: 34px; height: 34px;">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="${fill}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: ${color}; transition: all 0.2s ease; margin-top: 2px;">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
        </svg>
      </button>
    `;
    }
  }
}
customElements.define('favorites-button', FavoritesButton);

/**
 * <favorites-counter></favorites-counter>
 */
class FavoritesCounter extends HTMLElement {
  connectedCallback() {
    this.render();

    this.updateHandler = () => this.render();
    window.addEventListener('favorites:updated', this.updateHandler);

    /*
     * Shopify/theme code can re-render or morph header contents after
     * this custom element has already connected. If that replaces the
     * inner markup, connectedCallback does not necessarily run again.
     *
     * Watch only this component and restore its correct rendered state
     * when external code changes its children.
     */
    this.mutationObserver = new MutationObserver(() => {
      if (this._isRendering) return;

      const expectedCount = FavoritesManager.getFavorites().length;
      const bubble = this.querySelector('.favorites-count-bubble');
      const renderedCount = bubble
        ? parseInt(bubble.textContent.trim(), 10)
        : 0;

      const hasButton = !!this.querySelector('.favorites-drawer-toggle');

      if (!hasButton || renderedCount !== expectedCount) {
        this.render();
      }
    });

    this.mutationObserver.observe(this, {
      childList: true,
      subtree: true
    });
  }

  disconnectedCallback() {
    window.removeEventListener('favorites:updated', this.updateHandler);

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  render() {
    if (this._isRendering) return;
    this._isRendering = true;

    const count = FavoritesManager.getFavorites().length;

    if (count > 0) {
      this.innerHTML = `
        <button
          class="favorites-drawer-toggle header-actions__action"
          style="
            background:none;
            border:none;
            padding:0;
            position:relative;
            display:flex;
            align-items:center;
            justify-content:center;
            height:100%;
            cursor:pointer;
          "
          aria-label="Favorites (${count})"
          title="Favorites (${count})"
        >
          <span
            class="svg-wrapper"
            aria-hidden="true"
            style="
              width:24px;
              height:24px;
              display:flex;
              align-items:center;
              justify-content:center;
            "
          >
            <svg
              class="icon icon-heart"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </span>

          <span
            class="favorites-count-bubble"
            aria-hidden="true"
            style="
              position:absolute;
              top:-6px;
              right:-8px;
              min-width:18px;
              height:18px;
              padding:0 5px;
              border-radius:999px;
              background:#000;
              color:#fff;
              display:flex;
              align-items:center;
              justify-content:center;
              box-sizing:border-box;
              font-size:10px;
              font-weight:600;
              line-height:1;
              z-index:2;
            "
          >
            ${count}
          </span>
        </button>
      `;
    } else {
      this.innerHTML = `
        <button
          class="favorites-drawer-toggle header-actions__action"
          style="
            background:none;
            border:none;
            padding:0;
            position:relative;
            display:flex;
            align-items:center;
            justify-content:center;
            height:100%;
            cursor:pointer;
          "
          aria-label="Favorites"
          title="Favorites"
        >
          <span
            class="svg-wrapper"
            aria-hidden="true"
            style="
              width:24px;
              height:24px;
              display:flex;
              align-items:center;
              justify-content:center;
            "
          >
            <svg
              class="icon icon-heart"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </span>
        </button>
      `;
    }

    const btn = this.querySelector('.favorites-drawer-toggle');

    if (btn) {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const drawer = document.getElementById('favorites-drawer');
        const overlay = document.getElementById('favorites-overlay');

        if (drawer && overlay) {
          drawer.style.right = '0';
          overlay.style.display = 'block';
        } else {
          window.location.href = '/pages/favorites';
        }
      });
    }

    this._isRendering = false;
  }
}

customElements.define('favorites-counter', FavoritesCounter);

/**
 * <favorites-grid></favorites-grid>
 */
class FavoritesGrid extends HTMLElement {
  connectedCallback() {
    this.render();
    this.updateHandler = () => this.render();
    window.addEventListener('favorites:updated', this.updateHandler);
  }

  disconnectedCallback() {
    window.removeEventListener('favorites:updated', this.updateHandler);
  }

  render() {
    const favorites = FavoritesManager.getFavorites();
    
    if (favorites.length === 0) {
      this.innerHTML = `
        <div class="favorites-empty" style="text-align: center; padding: 4rem 1rem;">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" style="color: #ccc; margin-bottom: 1rem;"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
          <h2 class="h3">Your wishlist is empty</h2>
          <p>Browse our products and click the heart icon to save them for later.</p>
          <a href="/collections/all" class="button button--primary" style="margin-top: 1rem;">Continue Shopping</a>
        </div>
      `;
      return;
    }

    let cardsHtml = favorites.map(item => {
      // Clean up legacy broken prices (remove span tags)
      let cleanPrice = item.price || '';
      // Strip out any HTML tags that might have been cached in older versions
      if (cleanPrice.includes('<') && cleanPrice.includes('>')) {
        const temp = document.createElement('div');
        temp.innerHTML = cleanPrice;
        cleanPrice = temp.textContent || temp.innerText || '';
      }
      
      let imgUrl = item.image || '';
      if (imgUrl && !imgUrl.includes('format=')) {
        imgUrl += (imgUrl.includes('?') ? '&' : '?') + 'width=600&format=webp';
      }

      return `
        <li style="list-style: none; margin: 0; padding: 0;">
          <div class="vertex-grid-item" style="display: flex; flex-direction: column; width: 100%; height: 100%;">
            <div class="vertex-card-wrapper" style="width: 100%;">
              <div class="vertex-card">
                <div class="vertex-card__inner" style="position: relative; aspect-ratio: 4/5; background: #f5f5f5; border-radius: 6px; overflow: hidden; display: block; width: 100%;">
                  <div style="position: absolute; top: 8px; right: 8px; z-index: 2;">
                    <favorites-button product-handle="${item.handle}" product-title="${item.title}" product-image="${item.image}" product-price="${cleanPrice}" product-url="${item.url}"></favorites-button>
                  </div>
                  <a href="${item.url}" class="vertex-unstyled-link" style="display: block; width: 100%; height: 100%;">
                    <img src="${imgUrl}" alt="${item.title}" loading="lazy" width="400" height="500" class="motion-reduce" style="width: 100%; height: 100%; object-fit: cover; display: block;">
                  </a>
                </div>
                <div class="vertex-card__content" style="padding-top: 8px; display: block;">
                  <div class="vertex-card__information" style="display: block; text-align: left;">
                    <h3 class="vertex-card__heading h5" style="margin-bottom:0; font-weight: 900; text-transform: uppercase; letter-spacing: 0.05em; font-size: 12px; line-height: 1.3; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;">
                      <a href="${item.url}" class="vertex-unstyled-link" style="text-decoration:none; color: #000;">${item.title}</a>
                    </h3>
                    <div class="vertex-card-information" style="margin-top: 4px;">
                      <span class="price" style="font-weight:900; color:#000; font-size:15px; display:block; margin-top:2px; letter-spacing: 0.05em;">${cleanPrice}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </li>
    `}).join('');

    this.innerHTML = `
      <style>
        favorites-grid[layout="drawer"] .fav-grid {
          grid-template-columns: repeat(2, 1fr) !important;
          gap: 12px !important;
        }
        .fav-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 16px;
          list-style: none;
          padding: 0;
          margin: 0;
        }
        @media (max-width: 1200px) {
          .fav-grid { grid-template-columns: repeat(4, 1fr); }
        }
        @media (max-width: 989px) {
          .fav-grid { grid-template-columns: repeat(3, 1fr); gap: 15px; }
        }
        @media (max-width: 768px) {
          .fav-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
        }
      </style>
      <ul class="fav-grid">
        ${cardsHtml}
      </ul>
    `;
  }
}
customElements.define('favorites-grid', FavoritesGrid);
